/* eslint-disable */
/*
 * First level chats, includes:
 * - chats with context of the patient (eg. patients itself)
 * - private one-to-one and group chats (eg. social)
 *
 * Component uses virtual list component for large lists handling
 */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { List } from 'react-virtualized';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';

import ListItem from './ListItem';
import EmptyNavigation from '../../common/EmptyState/Navigation';
import Loader from '../../common/Loader';

import { CHAT_EMPTY_NAVIGATION } from '../../../constants/strings';
import { INTRO_HINT_SELECTORS } from '../../common/Intro';

import { actions as settingsActions } from '../../../redux/board/settings';
import {
  loadingStateSelector,
  sortedChannelsSelector,
  totalChannelsSelector,
} from '../../../redux/board/selectors/channels';
import { activeChannelIdSelector } from '../../../redux/chatConnection/selectors';
import {
  targetUserIdSelector,
  targetUserSelector,
} from '../../../redux/context/selectors';
import { actions as chatConnectionActions } from '../../../redux/chatConnection';
import { isDesktop } from '../../../utils/mobileHelpers';
import InfiniteScroll from '../../../utils/infiniteScroll';
import isElementVisible from '../../../utils/isElementVisible';
import { showDependentCreateModal } from '../../../redux/ui/popup';
import { dependentStatusCheck } from '../../../utils/dependentStatusCheck';

const PAGE_SIZE = 20;
const SIDEBAR_WIDTH = 300;
const SCROLLBAR_WIDTH = 8;
const ITEM_HEIGHT = 50;

const mapStateToProps = state => ({
  list: sortedChannelsSelector(state),
  sending: loadingStateSelector(state),
  targetUserId: targetUserIdSelector(state),
  targetUser: targetUserSelector(state),
  totalUsers: totalChannelsSelector(state),
  activeChannelId: activeChannelIdSelector(state),
});

const mapDispatchToProps = {
  loadNextPage: settingsActions.loadNextPage,
  setTargetUserId: settingsActions.setLastUserId,
  showDependentCreatedModal: showDependentCreateModal,
  removeLastSocialId: settingsActions.removeLastSocialId,
  setActiveChannelId: chatConnectionActions.setActiveChannelId,
  selectSocialChat: chatConnectionActions.selectSocialChat,
};

/* eslint-disable no-unused-expressions */
@injectIntl
@connect(
  mapStateToProps,
  mapDispatchToProps,
)
export default class ChatsList extends PureComponent {
  static propTypes = {
    list: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
      }),
    ),
    sending: PropTypes.bool,
    loadNextPage: PropTypes.func,
    minimized: PropTypes.bool,
    onSelect: PropTypes.func,
    setTargetUserId: PropTypes.func,
    showDependentCreatedModal: PropTypes.func,
    targetUserId: PropTypes.number,
    totalUsers: PropTypes.number,
    intl: PropTypes.shape({
      formatMessage: PropTypes.func,
    }),
    patientPortalContext: PropTypes.bool,
    patientPortalARList: PropTypes.array,
  };

  componentDidMount() {
    // initialize scroll on non empty container
    if (!this.infiniteScroll && this.listNode) {
      this.initInfiniteScroll();
    }

    // handle selected item visibility on pages switching
    this.scrollItemIntoView(this.props.targetUserId);
  }

  componentDidUpdate(prevProps) {
    // initialize scroll on non empty container
    if (!this.infiniteScroll && this.listNode) {
      this.initInfiniteScroll();
    }

    // uninitialize scroll on filters changing
    if (
      this.infiniteScroll &&
      isEmpty(this.props.list) &&
      !isEmpty(prevProps.list)
    ) {
      this.unscribeInfiniteScroll();
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.activeChannelId !== this.props.activeChannelId) {
      this.scrollItemIntoView(nextProps.activeChannelId);
    }
  }

  componentWillUnmount() {
    this.infiniteScroll && this.unscribeInfiniteScroll();
  }

  checkActive = item => {
    const { activeChannelId, targetUserId } = this.props;

    if (get(item, 'contextUser')) {
      return targetUserId === get(item, 'contextUser.id', null);
    }
    if (get(item, 'contextChannel')) {
      return get(item, 'contextChannel.channelId', null) === activeChannelId;
    }
  };

  checkActiveAndDefault(items, item, i) {
    if (i > 0) return this.checkActive(item);

    return items.filter(item => this.checkActive(item)).length
      ? this.checkActive(item)
      : i === 0;
  }

  get listItems() {
    const { minimized, patientPortalContext } = this.props;

    return this.safeList.map((item, i) => (
      <ListItem
        key={i}
        {...{ minimized }}
        active={this.checkActiveAndDefault(this.safeList, item, i)}
        onClick={(id, type) => this.handleSelect(id, type)}
        intro={i === 0 ? INTRO_HINT_SELECTORS.messages.selectChat : null}
        patientPortalContext={patientPortalContext}
        {...item}
      />
    ));
  }

  get content() {
    if (isEmpty(this.safeList)) return null;

    if (this.safeList.length < PAGE_SIZE) return this.listItems;

    const sidebarWidth = isDesktop() ? SIDEBAR_WIDTH : window.innerWidth;
    const hasScrollBar = this.checkScrollBarPresence(this.listNode);

    return (
      <List
        width={hasScrollBar ? sidebarWidth - SCROLLBAR_WIDTH : sidebarWidth}
        height={this.safeList ? ITEM_HEIGHT * this.safeList.length : 0}
        rowCount={this.safeList.length}
        rowHeight={ITEM_HEIGHT}
        rowRenderer={this.rowRenderer}
        style={{ outline: 'none' }}
      />
    );
  }

  get hasNextPage() {
    const { list, totalUsers } = this.props;

    return !totalUsers || totalUsers > get(list, 'length', 0);
  }

  get safeList() {
    const { patientPortalARList, list, patientPortalContext } = this.props;

    return patientPortalContext ? patientPortalARList : list;
  }

  handleSelect(id, type) {
    const {
      onSelect,
      setTargetUserId,
      setActiveChannelId,
      onClose,
      targetUserId,
      removeLastSocialId,
      selectSocialChat,
      patientPortalARList,
      showDependentCreatedModal,
    } = this.props;

    const guardianDependentsList =
      patientPortalARList &&
      patientPortalARList.map(dependent => dependent.contextUser);

    const isVerified = dependentStatusCheck({
      id,
      guardianDependentsList,
      showDependentCreatedModal,
    });

    if (!isVerified) return;

    if (type) {
      selectSocialChat(id);
      onClose && onClose();
    } else {
      onSelect && onSelect();
      if (targetUserId === id) return;
      setActiveChannelId(null);
      removeLastSocialId();
      setTargetUserId({ id });
    }
    // this.scrollItemIntoView(id);
  }

  scrollItemIntoView(id) {
    const targetElement =
      this.listNode && this.listNode.querySelector(`li[data-id="${id}"]`);

    if (
      targetElement &&
      !isElementVisible(targetElement, get(this.listNode, 'parentNode'))
    ) {
      targetElement.scrollIntoView();
    }
  }

  loadMore = () => {
    if (this.hasNextPage) {
      const { loadNextPage } = this.props;

      loadNextPage && loadNextPage();
    }
  };

  initInfiniteScroll() {
    this.infiniteScroll = new InfiniteScroll({
      element: get(this.listNode, 'parentNode'),
      loadEdgeOffset: 100,
      type: 'lower',
      onLoad: this.loadMore,
    });
  }

  unscribeInfiniteScroll = () => {
    this.infiniteScroll.unsubscribe();
    this.infiniteScroll = null;
  };

  checkScrollBarPresence(listElem) {
    // handle pages switching scenario
    if (!listElem) return true;

    const listHeight = get(listElem, 'offsetHeight', 0);
    const wrapperHeight = get(listElem, 'parentNode.offsetHeight', 0);

    return listHeight > wrapperHeight;
  }

  rowRenderer = ({
    index,
    isScrolling,
    // isVisible,
    key,
    // parent,
    style,
  }) => (
    <div {...{ key, style }}>
      <Choose>
        <When condition={isScrolling}>
          <span />
        </When>
        <Otherwise>{this.listItems[index]}</Otherwise>
      </Choose>
    </div>
  );

  infiniteScroll = null;

  listNode = null;

  render() {
    const {
      sending,
      intl: { formatMessage },
    } = this.props;

    if (isEmpty(this.safeList) && !sending) {
      return <EmptyNavigation text={formatMessage(CHAT_EMPTY_NAVIGATION)} />;
    }

    return (
      <div ref={node => (this.listNode = node)}>
        {this.content}
        <If condition={sending}>
          <Loader white />
        </If>
      </div>
    );
  }
}
/* eslint-enable no-unused-expressions */



// WEBPACK FOOTER //
// ./src/components/Messages/ChatsList/index.jsx